import { useEffect, useMemo, useState } from 'react'
import { Link, PageProps } from 'gatsby'
import clsx from 'clsx'
import {
  ProductsQuery,
  ProductsQueryResult,
  ProductsQueryVariables,
  ProductStatus,
  useProductsQuery,
} from 'lib/graphql/storeadmin'
import { usePagination } from 'lib/hooks'
import {
  Button,
  Card,
  Error as ErrorComponent,
  Pagination,
  SEO,
  Space,
  Title,
} from 'components/common'
import { Product, ProductSearch, ProductTable } from 'components/products'
import { datetime } from 'lib/str-formatter'

export const Head = () => <SEO title="施設管理" />

const IndexPage = (props: PageProps<Queries.Query>): JSX.Element => {
  const [queryVariables, setQueryVariables] = useState<ProductsQueryVariables>({
    status: null,
  })
  const [searchResult, setSearchResult] = useState(null)
  const [warningMessage, setWarningMessage] = useState(null)

  const { data, loading, error, goto, refetch, page, size, paginate } =
    usePagination<ProductsQuery, ProductsQueryVariables, ProductsQueryResult>({
      initialSize: 10,
      queryOptions: { variables: queryVariables },
      queryHooks: useProductsQuery,
    })

  const dataLength = data?.products?.totalCount
  const dataSource = useMemo<Product[]>(() => {
    return (
      data?.products?.edges?.map(({ product }) => {
        const {
          id,
          name,
          status,
          variants,
          images,
          approvalRequired,
          createdAt,
          updatedAt,
        } = product

        const variantsRange =
          variants?.edges?.length > 1 && variants?.edges?.length

        return {
          key: id,
          image: images?.[0],
          title: name.jaJp,
          // TODO: status を API から取得するようにする（現在 API に status フィールドが存在しない）
          status: status,
          variantsRange: variantsRange,
          approvalRequired: approvalRequired,
          createdAt: datetime(createdAt),
          updatedAt: datetime(updatedAt),
        }
      }) || []
    )
  }, [data])

  useEffect(() => {
    const checkErrorNotFound: boolean = error?.message?.toString()?.length <= 0
    if (checkErrorNotFound) {
      refetch(queryVariables)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryVariables]) // deps に refetch が含まれていると、永遠に refetch し続ける

  const onTabChange = (tab: string) => {
    goto(1)
    setWarningMessage(null)
    setSearchResult(null)
    switch (tab) {
      case ProductStatus.Active:
        return setQueryVariables({ status: ProductStatus.Active })
      case ProductStatus.Inactive:
        return setQueryVariables({ status: ProductStatus.Inactive })
      case ProductStatus.Archived:
        return setQueryVariables({ status: ProductStatus.Archived })
      default:
        return setQueryVariables({ status: null })
    }
  }

  const onSearch = () => {
    goto(1)
    if (!searchResult) {
      setWarningMessage('絞り込みたい内容を入力してください')
      return
    }
    setWarningMessage(null)
    setQueryVariables({
      ...queryVariables,
      name_Contains: searchResult?.replace(/\s/g, ''),
    })
  }
  const searchClear = () => {
    if (searchResult !== null) {
      goto(1)
      setWarningMessage(null)
      setSearchResult(null)
      setQueryVariables({
        ...queryVariables,
        name_Contains: null,
      })
    }
  }

  if (error) {
    return ErrorComponent.render(error)
  }

  return (
    <>
      <div className={clsx('flex', 'justify-between', 'items-center')}>
        <div>
          <Title level={1}>施設管理</Title>
        </div>
        <div className={clsx('flex', 'justify-end')}>
          <Button type="primary">
            <Link to="./new">施設を追加する</Link>
          </Button>
        </div>
      </div>

      <Card
        tabList={[
          { key: 'all', tab: 'すべて' },
          { key: ProductStatus.Active, tab: 'アクティブ' },
          { key: ProductStatus.Inactive, tab: '非アクティブ' },
          { key: ProductStatus.Archived, tab: 'アーカイブ済み' },
        ]}
        shadow
        onTabChange={onTabChange}
      >
        {/* filter search input*/}
        <ProductSearch
          onSearch={onSearch}
          searchClear={searchClear}
          setSearchResult={setSearchResult}
          searchResult={searchResult}
          warningMessage={warningMessage}
        />
        <Space direction="vertical" size="large">
          <ProductTable dataSource={dataSource} loading={loading} />
          <Pagination
            current={page}
            pageSize={size}
            total={dataLength}
            onChange={paginate}
            showSizeChanger
          />
        </Space>
      </Card>
    </>
  )
}

export default IndexPage
